/**
 * Title/Escrow Company Types
 *
 * Types for managing title and escrow companies with fee schedules
 * linked to Encompass itemization fees.
 */

/**
 * Company type - title, escrow, or both services
 */
export type CompanyType = 'title' | 'escrow' | 'both';

/**
 * Fee pricing type
 */
export type PricingType = 'fixed' | 'tiered' | 'percentage';

/**
 * Title/Escrow Company
 */
export interface TitleEscrowCompany {
  id: string;
  organizationId: string;
  name: string;
  companyType: CompanyType;
  legalName: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  mainPhone: string | null;
  mainEmail: string | null;
  fax: string | null;
  website: string | null;
  officerFirstName: string | null;
  officerLastName: string | null;
  officerEmail: string | null;
  officerPhone: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  isActive: boolean;
  isPreferred: boolean;
  notes: string | null;
  feeScheduleEffectiveDate: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

/**
 * Company Fee Schedule - links company to Encompass fee with pricing
 */
export interface CompanyFeeSchedule {
  id: string;
  companyId: string;
  feeId: string;
  pricingType: PricingType;
  amount: number | null;
  minLoanAmount: number | null;
  maxLoanAmount: number | null;
  percentageRate: number | null;
  calculationBase: string | null;
  minAmount: number | null;
  maxAmount: number | null;
  isConditional: boolean;
  conditionDescription: string | null;
  isActive: boolean;
  effectiveDate: string | null;
  expiryDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fee schedule with joined Encompass fee details
 */
export interface CompanyFeeScheduleWithFee extends CompanyFeeSchedule {
  fee: {
    id: string;
    name: string;
    encompassFeeId: string;
    sections: string[];
    ucdFee: string | null;
  };
}

/**
 * Grouped fee tiers for a single fee (for tiered pricing)
 */
export interface GroupedFeeTiers {
  feeId: string;
  feeName: string;
  encompassFeeId: string;
  sections: string[];
  pricingType: PricingType;
  isConditional: boolean;
  conditionDescription: string | null;
  tiers: FeeTier[];
}

/**
 * Single tier in a tiered fee structure
 */
export interface FeeTier {
  id: string;
  minLoanAmount: number | null;
  maxLoanAmount: number | null;
  amount: number | null;
  percentageRate: number | null;
  updatedAt?: string;
}

/**
 * Database row format (snake_case)
 */
export interface TitleEscrowCompanyRow {
  id: string;
  organization_id: string;
  name: string;
  company_type: string;
  legal_name: string | null;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  zip: string;
  main_phone: string | null;
  main_email: string | null;
  fax: string | null;
  website: string | null;
  officer_first_name: string | null;
  officer_last_name: string | null;
  officer_email: string | null;
  officer_phone: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  is_active: boolean;
  is_preferred: boolean;
  notes: string | null;
  fee_schedule_effective_date: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/**
 * Fee schedule row format (snake_case)
 */
export interface CompanyFeeScheduleRow {
  id: string;
  company_id: string;
  fee_id: string;
  pricing_type: string;
  amount: number | null;
  min_loan_amount: number | null;
  max_loan_amount: number | null;
  percentage_rate: number | null;
  calculation_base: string | null;
  min_amount: number | null;
  max_amount: number | null;
  is_conditional: boolean;
  condition_description: string | null;
  is_active: boolean;
  effective_date: string | null;
  expiry_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Transform company row to frontend model
 */
export function transformCompanyRow(row: TitleEscrowCompanyRow): TitleEscrowCompany {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    companyType: row.company_type as CompanyType,
    legalName: row.legal_name,
    addressLine1: row.address_line1,
    addressLine2: row.address_line2,
    city: row.city,
    state: row.state,
    zip: row.zip,
    mainPhone: row.main_phone,
    mainEmail: row.main_email,
    fax: row.fax,
    website: row.website,
    officerFirstName: row.officer_first_name,
    officerLastName: row.officer_last_name,
    officerEmail: row.officer_email,
    officerPhone: row.officer_phone,
    contactFirstName: row.contact_first_name,
    contactLastName: row.contact_last_name,
    isActive: row.is_active,
    isPreferred: row.is_preferred,
    notes: row.notes,
    feeScheduleEffectiveDate: row.fee_schedule_effective_date,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by
  };
}

/**
 * Transform fee schedule row to frontend model
 */
export function transformFeeScheduleRow(row: CompanyFeeScheduleRow): CompanyFeeSchedule {
  return {
    id: row.id,
    companyId: row.company_id,
    feeId: row.fee_id,
    pricingType: row.pricing_type as PricingType,
    amount: row.amount,
    minLoanAmount: row.min_loan_amount,
    maxLoanAmount: row.max_loan_amount,
    percentageRate: row.percentage_rate,
    calculationBase: row.calculation_base,
    minAmount: row.min_amount,
    maxAmount: row.max_amount,
    isConditional: row.is_conditional,
    conditionDescription: row.condition_description,
    isActive: row.is_active,
    effectiveDate: row.effective_date,
    expiryDate: row.expiry_date,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

/**
 * Create company input (for creating new company)
 */
export interface CreateTitleEscrowCompanyInput {
  name: string;
  companyType: CompanyType;
  legalName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  mainPhone?: string;
  mainEmail?: string;
  fax?: string;
  website?: string;
  officerFirstName?: string;
  officerLastName?: string;
  officerEmail?: string;
  officerPhone?: string;
  contactFirstName?: string;
  contactLastName?: string;
  isPreferred?: boolean;
  notes?: string;
}

/**
 * Update company input
 */
export interface UpdateTitleEscrowCompanyInput extends Partial<CreateTitleEscrowCompanyInput> {
  isActive?: boolean;
}

/**
 * Create fee schedule input
 */
export interface CreateFeeScheduleInput {
  companyId: string;
  feeId: string;
  pricingType: PricingType;
  amount?: number;
  minLoanAmount?: number;
  maxLoanAmount?: number;
  percentageRate?: number;
  calculationBase?: string;
  minAmount?: number;
  maxAmount?: number;
  isConditional?: boolean;
  conditionDescription?: string;
  notes?: string;
}

/**
 * Company filter options
 */
export interface CompanyFilters {
  companyType?: CompanyType | 'all';
  isActive?: boolean;
  isPreferred?: boolean;
  state?: string;
  search?: string;
}

/**
 * Company stats
 */
export interface CompanyStats {
  total: number;
  title: number;
  escrow: number;
  both: number;
  preferred: number;
  active: number;
}
